const { isArray } = Array;

function baseIsPrimitive(kindOrPredicate) {
    const isKind = typeof kindOrPredicate === 'string';
    return function baseIsPrimitiveOfKind(tag) {
        return function baseIsPrimitiveOfKindTag(value) {
            const t = typeof value;
            return (isKind && t === kindOrPredicate)
                || (!isKind && kindOrPredicate && kindOrPredicate(value))
                || (t === 'object'
                    && ((isArray(tag) && tag.includes(Object.prototype.toString.call(value)))
                        || Object.prototype.toString.call(value) === tag));
        };
    };
}

const isDate = /* #__PURE__ */ baseIsPrimitive()([
    '[object Date]',
]);

const isNumber = /* #__PURE__ */ baseIsPrimitive('number')('[object Number]');

/**
 * ECMA standard "date time string format" is: `YYYY-MM-DDTHH:mm:ss.sssZ`
 *
 * Various components can be omitted, so the following are all valid:
 *  - Date-only form: YYYY, YYYY-MM, YYYY-MM-DD
 *  - Date-time form: one of the above date-only forms, followed by T, followed by HH:mm, HH:mm:ss, or HH:mm:ss.sss. Each combination can be followed by a time zone offset.
 * For example, "2011-10-10" (date-only form), "2011-10-10T14:48:00" (date-time form), or "2011-10-10T14:48:00.000+09:00" (date-time form with milliseconds and time zone) are all valid date time strings.
 *
 * When the time zone offset is absent, date-only forms are interpreted as a UTC time and date-time forms are interpreted as a local time. The interpretation as a UTC time is due to a historical spec error that was not consistent with ISO 8601 but could not be changed due to web compatibility.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
 */
const interpretedAsUtcRegExp = /(Z|\+00:?00)$|^\d{13}$|^\d{10}$|^\d{4}(-\d{2}(-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?(Z|\+00:?00))?)?)?$/;
const offsetRegExp = /Z$|([+-])(\d{2}):?(\d{2})/;
const detectDateStringOffset = (input) => {
    const inputString = String(input);
    if (interpretedAsUtcRegExp.test(inputString)) {
        return 0;
    }
    const result = inputString.match(offsetRegExp);
    if (!result) {
        return null;
    }
    if (result[0] === 'Z') {
        return 0;
    }
    const sign = result[1];
    const hours = Number(result[2]);
    const minutes = Number(result[3]);
    const total = (hours * 60) + minutes;
    return total && sign === '+' ? (total * -1) : total;
};

/**
 * Converts raw number, string, or Date to a Date object.
 */
const toDate = (input) => {
    const inputAsString = String(input);
    // Check if input is a raw UNIX timestamp (must convert to Date)
    if (inputAsString.match(/^\d{5,}$$/)) {
        if (inputAsString.length === 10) {
            return new Date(Number(inputAsString) * 1000);
        }
        if (inputAsString.length === 13) {
            return new Date(Number(inputAsString));
        }
        return null;
    }
    try {
        return new Date(input);
    }
    catch {
        return null;
    }
};
const dateToString = (input, asUtcOrTimezoneOffset, glue = '/') => {
    let timezoneOffset = 0;
    const date = toDate(input);
    const localOffset = date?.getTimezoneOffset() ?? 0;
    if (isDate(input)) {
        if (isNumber(asUtcOrTimezoneOffset)) {
            timezoneOffset = (asUtcOrTimezoneOffset * -1) + localOffset;
        }
    }
    else {
        const inputOffset = detectDateStringOffset(input);
        if (isNumber(inputOffset)) {
            // if there is an offset is in the input, we need to invert it
            const effectiveInputOffset = inputOffset ? (inputOffset * -1) : 0;
            timezoneOffset = effectiveInputOffset + localOffset;
        }
    }
    // Invalid date
    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }
    // apply offset
    date.setMinutes(date.getMinutes() + timezoneOffset);
    return `${date.getFullYear()}${glue}${(`0${date.getMonth() + 1}`).slice(-2)}${glue}${(`0${date.getDate()}`).slice(-2)}`;
};

// jQuery Datepicker
function getDateFromJqueryDatepicker(selector, jQuery) {
    // needs jQuery
    if (!jQuery || !jQuery.fn.datepicker) {
        throw Error('jQuery or Datepicker missing');
    }
    // get date
    const date = jQuery(selector).datepicker('getDate');
    if (!date || (Object.prototype.hasOwnProperty.call(date, 'length') && date.length === 0)) {
        return undefined;
    }
    return date;
}
// Bootstrap Datepicker
function getDateFromBootstrapDatepicker(selector, jQuery) {
    // needs jQuery
    if (!jQuery || !jQuery.fn.datepicker) {
        throw new Error('jQuery or Datepicker missing');
    }
    // get date
    const date = jQuery(selector).data('datepicker').getDate();
    if (!date || (Object.prototype.hasOwnProperty.call(date, 'length') && date.length === 0)) {
        return undefined;
    }
    return date;
}
// Pickadate Datepicker
function getDateFromPickadateDatepicker(selector, jQuery) {
    // needs jQuery
    if (!jQuery || !jQuery.fn.pickadate) {
        throw new Error('jQuery or Pickadate missing');
    }
    // get date
    let date = jQuery(selector).pickadate('picker');
    date = date && date.get('select').obj;
    if (!date || (Object.prototype.hasOwnProperty.call(date, 'length') && date.length === 0)) {
        return undefined;
    }
    return date;
}
function getDateFromDatepicker(selector, options) {
    // defaults
    options = options || {};
    options.type = options.type || 'jquery';
    if (options.raw !== true) {
        options.raw = false;
    }
    // needs jQuery
    const $ = options.jQuery || globalThis.jQuery;
    // date
    let date;
    switch (options.type) {
        case 'bootstrap':
            date = getDateFromBootstrapDatepicker(selector, $);
            break;
        case 'pickadate':
            date = getDateFromPickadateDatepicker(selector, $);
            break;
        case 'jquery':
        default:
            date = getDateFromJqueryDatepicker(selector, $);
            break;
    }
    if (date && options.raw === false) {
        // as thn string
        date = dateToString(date);
    }
    return date;
}

export { getDateFromDatepicker as g };
